import { useTranslation } from 'react-i18next';
import { Bot, Unplug } from 'lucide-react';
import { pickProse } from '@/lib/why/prose';
import { formatInstant } from '@/lib/why/format';
import type { WhyView } from '@/lib/why/types';

/**
 * The account on top: the rolling summary the disruption wrote about itself.
 *
 * THREE ABSENCES, THREE DIFFERENT ANSWERS — collapsing them is exactly what
 * this feature exists to stop:
 *
 *   · `narrative.known:false` — we could not READ the journal (unwired, or the
 *     upstream did not answer). The named reason is shown, and the LIVE
 *     evidence below still stands.
 *   · `summary:null` with chapters present — the journal is readable, the
 *     rolling account simply has not been re-authored yet. The chapters below
 *     are the record.
 *   · a summary that exists — rendered VERBATIM in the operator's language,
 *     with its provenance (version, model, when) attached so it reads as
 *     evidence rather than as chrome.
 *
 * The prose is never translated in the client — it arrives bilingual from the
 * one guarded generation (see `lib/why/prose.ts`).
 */
export function WhyAccount({ view }: { view: WhyView }) {
  const { t, i18n } = useTranslation('why');
  const lang = i18n.resolvedLanguage ?? i18n.language;
  const summary = view.summary;

  return (
    <section data-testid="why-account" data-known={view.narrative?.known ? 'true' : 'false'}>
      <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {t('account.heading')}
      </h3>

      {!view.narrative?.known ? (
        <p
          className="flex items-start gap-1.5 rounded-md bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:bg-amber-950/40 dark:text-amber-300"
          data-testid="why-account-gap"
          data-gap={view.narrative?.reason ?? ''}
        >
          <Unplug className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden />
          {view.narrative?.reason
            ? t(`account.gap.${view.narrative.reason}`, {
                defaultValue: t('account.gap.UPSTREAM_UNREADABLE'),
              })
            : t('account.gap.UPSTREAM_UNREADABLE')}
        </p>
      ) : summary ? (
        <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="why-account-summary">
          <h4
            className="text-sm font-bold leading-snug text-foreground"
            data-testid="why-account-headline"
          >
            {pickProse(summary.prose?.headline, lang)}
          </h4>
          <p
            className="mt-1 whitespace-pre-line text-xs leading-relaxed text-foreground/90"
            data-testid="why-account-body"
          >
            {pickProse(summary.prose?.body, lang)}
          </p>
          <p
            className="mt-2 flex flex-wrap items-center gap-1.5 text-[10.5px] text-muted-foreground"
            data-testid="why-account-provenance"
          >
            <Bot className="h-3 w-3 shrink-0" aria-hidden />
            {t('account.provenance', {
              model: summary.model,
              version: summary.version,
              at: formatInstant(summary.generatedAt),
            })}
          </p>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground" data-testid="why-account-not-authored">
          {t('account.notAuthored')}
        </p>
      )}
    </section>
  );
}
